/** One staff member in the roster: who they are, how they are paid, their four
 *  rates and their workshop counts. The pay cells show when the table has pay
 *  columns; a row the API sent no rates for gets dashes. A per-shift person with no rate of their own is flagged. */
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { StaffMember } from '../../../api/staff';

import StaffPayCells from './StaffPayCells';

const CENTER = 'px-4 py-3 text-center text-sm text-bark-900 dark:text-[#e8ece9]';

export default function StaffRosterRow({
  member,
  showPay,
}: {
  member: StaffMember;
  showPay: boolean;
}) {
  const navigate = useNavigate();
  const rates = member.currentRates;
  const isSalaried = member.isSalaried === true;
  const hasNoRates = !!rates && !isSalaried && rates.every((r) => r.source !== 'personal');

  return (
    <tr
      onClick={() => navigate(`/admin/staff/${member.id}`)}
      className="group hover:bg-stone-50 dark:hover:bg-[#242d28] cursor-pointer transition-colors"
    >
      <td className="sticky left-0 z-10 bg-white dark:bg-[#1a1f1c] group-hover:bg-stone-50 dark:group-hover:bg-[#242d28] px-6 py-3">
        <div className="flex items-center gap-2">
          <div>
            <div className="font-medium text-bark-900 dark:text-[#e8ece9] text-sm">
              {member.name}
            </div>
            <div className="text-xs text-stone-500 dark:text-stone-400">{member.email}</div>
          </div>
          {hasNoRates && (
            <span
              role="img"
              aria-label="No rates set"
              title="No rates set"
              className="flex-shrink-0"
            >
              <AlertCircle size={14} className="text-clay-500" aria-hidden="true" />
            </span>
          )}
        </div>
      </td>
      {showPay && <StaffPayCells rates={rates} isSalaried={isSalaried} />}
      <td className={CENTER}>{member.workshopCount}</td>
      <td className="px-4 py-3 text-center text-sm text-stone-500 dark:text-stone-400">
        {member.upcomingWorkshops}
      </td>
    </tr>
  );
}
